<script>
import axios from "axios";
import { toast, variants } from "@/helpers/toaster";

export default {
  name: "CityExpressSettings",
  data() {
    return {
      loading: false,
      saving: false,
      layoutsLoading: false,
      layoutsError: false,
      layouts: [],
      form: {
        api_key: "",
        api_base_url: "https://api.cityexpress.rs",
        default_country_code: "RS",
        default_sender_id: null,
        label_layout_id: null,
        wings_otpremnica_prefixes: ["OR-", "OIF-"],
      },
      apiKeySet: false,
      defaultSender: null,
      senders: [],
      resetting: false,
    };
  },
  async mounted() {
    await Promise.all([this.loadSettings(), this.loadSenders(), this.loadLayouts()]);
  },
  computed: {
    savedLayoutMissing() {
      return this.form.label_layout_id !== null &&
        !this.layouts.some((layout) => layout.id === this.form.label_layout_id);
    },
  },
  methods: {
    async loadSettings() {
      this.loading = true;
      try {
        const { data } = await axios.get("/super-admin/city-express/settings");
        const s = data.data || {};
        this.apiKeySet = !!s.api_key_set;
        this.defaultSender = s.default_sender || null;
        this.form = {
          api_key: "",
          api_base_url: s.api_base_url || "https://api.cityexpress.rs",
          default_country_code: s.default_country_code || "RS",
          default_sender_id: s.default_sender_id || null,
          label_layout_id: s.label_layout_id == null ? null : Number(s.label_layout_id),
          wings_otpremnica_prefixes:
            Array.isArray(s.wings_otpremnica_prefixes) &&
            s.wings_otpremnica_prefixes.length
              ? s.wings_otpremnica_prefixes.slice()
              : ["OR-", "OIF-"],
        };
      } finally {
        this.loading = false;
      }
    },
    async loadSenders() {
      const { data } = await axios.get("/super-admin/city-express/senders");
      this.senders = data.data || [];
    },
    async loadLayouts() {
      this.layoutsLoading = true;
      this.layoutsError = false;
      try {
        const { data } = await axios.get("/super-admin/city-express/settings/layouts");
        this.layouts = data.data || [];
      } catch (e) {
        this.layoutsError = true;
      } finally {
        this.layoutsLoading = false;
      }
    },
    async submit() {
      this.saving = true;
      try {
        const payload = { ...this.form };
        if (!payload.api_key) delete payload.api_key;
        const { data } = await axios.put(
          "/super-admin/city-express/settings",
          payload,
        );
        const s = data.data || {};
        this.apiKeySet = !!s.api_key_set;
        this.defaultSender = s.default_sender || null;
        this.form.label_layout_id = s.label_layout_id == null ? null : Number(s.label_layout_id);
        this.form.api_key = "";
        toast("Podešavanja sačuvana.", variants.SUCCESS);
        await this.loadLayouts();
      } catch (e) {
        const response = e.response && e.response.data;
        const layoutErrors = response && response.errors && response.errors.label_layout_id;
        toast(
          (layoutErrors && layoutErrors[0]) ||
            (response && response.message) || "Greška pri čuvanju podešavanja.",
          variants.DANGER,
        );
      } finally {
        this.saving = false;
      }
    },
    /**
     * Wipe every shipment + linked otpremnica row in our DB. Used by
     * developers/QA to re-shoot the same batch of Wings otpremnice during
     * testing without polluting CE's side. Confirmation gate kept simple
     * because this is super-admin only and the operation is reversible
     * only by re-creating the shipments.
     */
    async resetShipments() {
      const ok = window.confirm(
        "Obrisaće SVE City Express pošiljke iz naše baze podataka. " +
          "Pošiljke kreirane kod CE-a OSTAJU kod njih.\n\n" +
          "Sigurni ste?",
      );
      if (!ok) return;
      this.resetting = true;
      try {
        const { data } = await axios.post(
          "/super-admin/city-express/settings/reset-shipments",
        );
        const n = (data.data && data.data.deleted_shipments) || 0;
        toast(`Obrisano ${n} pošiljki.`, variants.SUCCESS);
      } catch (e) {
        toast(
          (e.response && e.response.data && e.response.data.message) ||
            "Greška pri resetu.",
          variants.DANGER,
        );
      } finally {
        this.resetting = false;
      }
    },
  },
};
</script>

<template>
  <div>
    <b-form @submit.prevent="submit()">
      <div class="row">
        <div class="col-md-6">
          <div class="form-group">
            <label>API ključ</label>
            <b-form-input
              v-model="form.api_key"
              type="password"
              :placeholder="
                apiKeySet ? '•••••••• (postavljen)' : 'Unesite API ključ'
              "
              autocomplete="new-password"
            />
            <small class="text-muted" v-if="apiKeySet">
              Polje ostavite prazno da zadržite postojeći ključ.
            </small>
          </div>

          <div class="form-group">
            <label>API base URL</label>
            <b-form-input v-model="form.api_base_url" required />
          </div>

          <div class="form-group">
            <label>Podrazumevani pošiljalac</label>
            <b-form-select v-model="form.default_sender_id">
              <option :value="null">— bez podrazumevanog —</option>
              <option v-for="s in senders" :key="s.id" :value="s.id">
                {{ s.name }} ({{ s.city }})
              </option>
            </b-form-select>
          </div>

          <div class="form-group">
            <label>Država (ISO-2)</label>
            <b-form-input
              v-model="form.default_country_code"
              maxlength="2"
              required
            />
          </div>
        </div>

        <div class="col-md-6">
          <div class="form-group">
            <label for="ce-label-layout">Podrazumevani format nalepnica</label>
            <div class="d-flex align-items-center">
              <b-form-select
                id="ce-label-layout"
                v-model="form.label_layout_id"
                :disabled="loading || layoutsLoading || layoutsError || saving"
              >
                <option :value="null">Podrazumevani format CE naloga</option>
                <option v-if="savedLayoutMissing" :value="form.label_layout_id">
                  Sačuvani format (ID {{ form.label_layout_id }})
                </option>
                <option v-for="layout in layouts" :key="layout.id" :value="layout.id">
                  {{ layout.name }} (ID {{ layout.id }}){{ layout.is_default ? ' — CE podrazumevani' : '' }}
                </option>
              </b-form-select>
              <b-button
                class="ml-2"
                variant="outline-secondary"
                :disabled="layoutsLoading || saving"
                title="Osveži formate nalepnica"
                aria-label="Osveži formate nalepnica"
                @click="loadLayouts()"
              >
                <i class="fas fa-sync-alt" aria-hidden="true"></i>
              </b-button>
            </div>
            <small v-if="layoutsLoading" class="text-muted" role="status">Učitavanje formata...</small>
            <small v-else-if="layoutsError" class="text-danger" role="alert">
              Formati nalepnica trenutno nisu dostupni.
            </small>
            <small v-else-if="!layouts.length" class="text-muted">
              Nema dostupnih formata nalepnica.
            </small>
          </div>

          <div class="form-group">
            <label>Wings prefiksi otpremnica</label>
            <b-form-tags
              v-model="form.wings_otpremnica_prefixes"
              separator=" ,;"
              :tag-validator="(t) => !!t && t.length <= 16"
              placeholder="npr. OR-, OIF-"
              remove-on-delete
            />
            <small class="text-muted">
              Lista prefiksa po kojima se otpremnice učitavaju iz Wings-a (jedan
              poziv po prefiksu). Promena prefiksa odmah poništava keš i lista
              se osvežava na sledećem pregledu.
            </small>
          </div>
        </div>
      </div>

      <div class="text-right">
        <b-button type="submit" variant="primary" :disabled="saving || loading">
          {{ saving ? "Čuvanje…" : "Snimi" }}
        </b-button>
      </div>
    </b-form>

    <hr class="mt-4" />

    <!-- Danger zone: dev/QA reset of all locally stored shipments. -->
    <div class="card border-danger">
      <div class="card-body">
        <h6 class="text-danger mb-2">Fabrički reset</h6>
        <p class="mb-2 small text-muted">
          Briše SVE City Express pošiljke i njihove veze sa otpremnicama iz naše
          baze — koristi se za čisto stanje prilikom testiranja. Otpremnice u
          Wings-u se ne diraju, a pošiljke već kreirane kod CE-a ostaju kod
          njih.
        </p>
        <b-button
          variant="danger"
          :disabled="resetting"
          @click="resetShipments()"
        >
          {{ resetting ? "Brišem…" : "Resetuj sve pošiljke" }}
        </b-button>
      </div>
    </div>
  </div>
</template>
